import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import LiveCard from "@/components/base/LiveCard";
import { liveItems, type LiveCategory } from "@/mocks/lives";
import { filterLives } from "@/utils/live";
import FilterBar, { type ChannelFilterValue } from "./components/FilterBar";

const PAGE_SIZE = 8;

const defaultFilter: ChannelFilterValue = {
  category: "all",
  status: "all",
  sort: "hot",
};

export default function Channels() {
  const [searchParams] = useSearchParams();
  const initialCategory = searchParams.get("category") as LiveCategory | null;

  const [filter, setFilter] = useState<ChannelFilterValue>({
    ...defaultFilter,
    category: initialCategory ?? "all",
  });
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [filter.category, filter.status, filter.sort]);

  const results = useMemo(() => filterLives(liveItems, filter), [filter]);
  const shown = results.slice(0, visible);

  const handleChange = (next: Partial<ChannelFilterValue>) => {
    setFilter((prev) => ({ ...prev, ...next }));
  };

  const handleReset = () => {
    setFilter(defaultFilter);
  };

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-8 md:px-6 md:py-12">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "频道分类" }]} />

      <div className="mt-5 mb-7">
        <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
          频道分类
        </h1>
        <p className="mt-3 max-w-2xl text-sm text-foreground-600">
          浏览全部直播频道，按类型、直播状态筛选并排序，快速找到你感兴趣的直播内容。
        </p>
      </div>

      <FilterBar value={filter} onChange={handleChange} onReset={handleReset} />

      <div className="mb-5 mt-8 flex items-center justify-between">
        <p className="text-sm text-foreground-600">
          共找到 <span className="font-semibold text-primary-300">{results.length}</span> 个直播
        </p>
      </div>

      {shown.length > 0 ? (
        <>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {shown.map((item) => (
              <LiveCard key={item.id} item={item} />
            ))}
          </div>

          {visible < results.length && (
            <div className="mt-9 flex justify-center">
              <button
                type="button"
                onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-6 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-500/60"
              >
                <i className="ri-add-line" />
                加载更多直播
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-lg border border-background-200/60 bg-background-100 px-6 py-20 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-secondary-100 text-foreground-500">
            <i className="ri-search-eye-line text-3xl" />
          </span>
          <h2 className="mt-5 font-heading text-lg font-bold text-foreground-950">暂无符合条件的直播</h2>
          <p className="mt-2 max-w-sm text-sm text-foreground-600">
            换个筛选条件试试，或者重置筛选查看全部直播内容。
          </p>
          <button
            type="button"
            onClick={handleReset}
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-refresh-line" />
            重置筛选
          </button>
        </div>
      )}
    </div>
  );
}